import React, { useState } from "react";
import { EonUiButton, EonUiInput, EonUiLink } from "@eon-ui/eon-ui-components-react";
import Modal from "../modal/Modal";
import useSearchStore from "../../store/searchStore";
import { TileDataInterface } from "@me8eon/fav_storage";
import { useTranslation } from "@me8eon/translation";
import "./TileComponent.scss";
import { useJwtToken } from "@me8eon/react_authentication";

interface NewTileComponentProps {
    toggleAllAppsModal?: () => void;
}

const NewTileComponent: React.FC<NewTileComponentProps> = ({ toggleAllAppsModal }) => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const { toggleFavourite, setPrefData } = useSearchStore();

    const [isOpen, setIsOpen] = useState(false);
    const [customTitle, setCustomTitle] = useState<string>("");
    const [customDescription, setCustomDescription] = useState<string>("");
    const [customUrl, setCustomUrl] = useState<string>("");

    // Error states
    const [titleInvalid, setTitleInvalid] = useState(false);
    const [urlInvalid, setUrlInvalid] = useState(false);

    const resetForm = () => {
        setCustomTitle("");
        setCustomDescription("");
        setCustomUrl("");
        setTitleInvalid(false);
        setUrlInvalid(false);
    };

    const toggleModal = () => {
        setIsOpen(prev => !prev);
    };

    const validateForm = (): boolean => {
        const trimmedTitle = customTitle.trim();
        const trimmedUrl = customUrl.trim();

        const isTitleValid = !!trimmedTitle;
        const isUrlValid = !!trimmedUrl;

        setTitleInvalid(!isTitleValid);
        setUrlInvalid(!isUrlValid);

        return isTitleValid && isUrlValid;
    };

    const handleSubmit: React.FormEventHandler = event => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        const trimmedTitle = customTitle.trim();
        const trimmedUrl = customUrl.trim();
        const trimmedDescription = customDescription.trim();

        const rawAlias = trimmedTitle.toLowerCase().replace(/[^a-z0-9]+/g, "");
        const alias = rawAlias || `custom_app_${Date.now().toString(36)}`;

        const customTile: TileDataInterface = {
            alias,
            title: trimmedTitle,
            subTitle: trimmedDescription || trimmedTitle,
            target: trimmedUrl,
            icon: "app.svg",
            status: false
        } as any;

        toggleFavourite(customTile, false);
        setPrefData(jwt);
        resetForm();
        setIsOpen(false);
        if (toggleAllAppsModal) {
            toggleAllAppsModal();
        }
    };

    return (
        <>
            <EonUiLink
                onClick={toggleModal}
                key={"addCustomTileLink"}
                icon="add"
                iconPosition="left"
                href="javascript:void(0)"
                title={t("allApps.customTileButton", "Add custom card")}
                text={t("allApps.customTileButton", "Add custom card")}
                size="small"
                scheme="red500"
            ></EonUiLink>
            <Modal
                toggleModal={isOpen}
                closeModal={() => {
                    resetForm();
                    toggleModal();
                }}
                isCloseIcon={false}
                closeOnBackdrop={false}
                modalClass="custom-apps-modal"
            >
                <form className="custom-apps-form" onSubmit={handleSubmit}>
                    <h2 className="custom-apps-form__title">
                        {t("allApps.CustomTileHeading", "Add custom app")}
                    </h2>
                    <div className="custom-apps-form__field">
                        <EonUiInput
                            type="text"
                            value={customTitle}
                            label={t("allApps.customTileTitleLabel", "Title")}
                            labelOutside
                            autocomplete={false}
                            required
                            invalid={titleInvalid}
                            errorMessage={t("allApps.titleRequired", "Card title is required")}
                            onValueChanged={e => {
                                setCustomTitle(e.target.value);
                                if (titleInvalid && e.target.value.trim()) {
                                    setTitleInvalid(false);
                                }
                            }}
                        />
                    </div>
                    <div className="custom-apps-form__field">
                        <EonUiInput
                            type="text"
                            value={customDescription}
                            label={t("allApps.customTileSubTitle", "Description")}
                            labelOutside
                            autocomplete={false}
                            onValueChanged={e => setCustomDescription(e.target.value)}
                        />
                    </div>
                    <div className="custom-apps-form__field">
                        <EonUiInput
                            type="text"
                            value={customUrl}
                            label={t("allApps.customTileUrlLabel", "Link URL")}
                            labelOutside
                            autocomplete={false}
                            required
                            invalid={urlInvalid}
                            errorMessage={t("allApps.linkRequired", "Card link is required")}
                            onValueChanged={e => {
                                setCustomUrl(e.target.value);
                                if (urlInvalid && e.target.value.trim()) {
                                    setUrlInvalid(false);
                                }
                            }}
                        />
                    </div>
                    <div style={{ display: "flex", gap: "24px", alignItems: "center", padding: "18px", justifyContent: "center" }}>
                        <EonUiButton
                            text={t("allApps.createCardBtn", "Create card")}
                            rank="primary"
                            size="small"
                            onClick={handleSubmit}
                        />
                        <EonUiButton
                            text={t("cancel")}
                            rank="secondary"
                            size="small"
                            onClick={e => {
                                e.preventDefault();
                                resetForm();
                                toggleModal();
                            }}
                        />
                    </div>
                </form>
            </Modal>
        </>
    );
};

export default NewTileComponent;
